import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';
import { Card } from '../ui';
import ScoreBadge from './ScoreBadge';
import { SkillTagList } from './SkillTag';
import ExpandableThinking from './ExpandableThinking';

export default function FeedbackPanel({
  questionNumber,
  questionPrompt,
  questionType,
  studentResponse,
  score,
  maxScore = 100,
  status, // 'correct', 'incorrect', or 'pending'
  feedback,
  thinking,
  skills,
  className,
  // MCQ-specific props
  options,
  correctAnswer,
  studentAnswer,
  isCorrect,
  explanation,
  // Short answer/essay props
  modelAnswer,
  rubric,
  // Table completion props
  placements,
}) {
  const { t } = useTranslation();
  const getTypeLabel = (type) => {
    const labels = {
      short_answer: 'Short Answer',
      multiple_choice: 'Multiple Choice',
      mcq: 'Multiple Choice',
      essay: 'Essay',
      fill_blank: 'Fill in the Blank',
      table_completion: 'Categorization',
    };
    return labels[type] || type;
  };

  const getStatusIndicator = () => {
    if (status === 'correct') {
      return (
        <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    if (status === 'incorrect') {
      return (
        <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        </span>
      );
    }
    return null;
  };

  const isMCQ = questionType === 'multiple_choice' || questionType === 'mcq';
  const isTableCompletion = questionType === 'table_completion';

  // Helper to check if an item is correctly placed in table_completion
  const isItemCorrectlyPlaced = (item, columnIndex) => {
    if (!options?.columns) return false;
    const column = options.columns[columnIndex];
    const correctItems = column?.correctItems || [];
    return correctItems.includes(item);
  };

  // Get which column an item should be in (for table_completion)
  const getCorrectColumnForItem = (item) => {
    if (!options?.columns) return -1;
    return options.columns.findIndex(col =>
      (col.correctItems || []).includes(item)
    );
  };

  return (
    <Card className={cn('transition-all duration-200', className)}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
            Question {questionNumber} • {getTypeLabel(questionType)}
          </div>
          <h3 className="font-medium text-gray-900 dark:text-white leading-relaxed">
            {questionPrompt}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {getStatusIndicator()}
          <ScoreBadge score={score} maxScore={maxScore} status={status} />
        </div>
      </div>

      <div className="h-px bg-gray-200 dark:bg-gray-700 my-4" />

      {/* MCQ Options Display */}
      {isMCQ && options && options.length > 0 ? (
        <div className="mb-6">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            {t('feedback.answerChoices')}
          </h4>
          <div className="space-y-2">
            {options.map((option) => {
              const isThisCorrect = option.key?.toUpperCase() === correctAnswer?.toUpperCase();
              const isThisSelected = option.key?.toUpperCase() === studentAnswer?.toUpperCase();
              const isWrongSelection = isThisSelected && !isThisCorrect;

              return (
                <div
                  key={option.key}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border-2 transition-all',
                    isThisCorrect && 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700',
                    isWrongSelection && 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700',
                    !isThisCorrect && !isWrongSelection && 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                  )}
                >
                  <span
                    className={cn(
                      'flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-sm font-semibold',
                      isThisCorrect && 'bg-green-500 text-white',
                      isWrongSelection && 'bg-red-500 text-white',
                      !isThisCorrect && !isWrongSelection && 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                    )}
                  >
                    {option.key}
                  </span>
                  <div className="flex-1">
                    <span
                      className={cn(
                        'text-sm',
                        isThisCorrect && 'text-green-800 dark:text-green-300 font-medium',
                        isWrongSelection && 'text-red-800 dark:text-red-300',
                        !isThisCorrect && !isWrongSelection && 'text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {option.text}
                    </span>
                    {isThisCorrect && (
                      <span className="ml-2 text-green-600 dark:text-green-400 text-sm font-medium">✓ {t('feedback.correctMark')}</span>
                    )}
                    {isThisSelected && !isThisCorrect && (
                      <span className="ml-2 text-red-600 dark:text-red-400 text-sm font-medium">✗ {t('feedback.incorrectMark')}</span>
                    )}
                    {isThisSelected && isThisCorrect && (
                      <span className="ml-2 text-green-600 dark:text-green-400 text-sm font-medium">{t('feedback.yourAnswer')}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Explanation for MCQ */}
          {explanation && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-700">
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1 uppercase">{t('questions.explanation')}</p>
              <p className="text-sm text-blue-900 dark:text-blue-200">{explanation}</p>
            </div>
          )}
        </div>
      ) : isTableCompletion && options?.columns ? (
        /* Table Completion Feedback */
        <div className="mb-6">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            {t('feedback.yourCategorization')}
          </h4>
          <div
            className={cn(
              'grid gap-4',
              options.columns.length === 2 && 'grid-cols-2',
              options.columns.length === 3 && 'grid-cols-3',
              options.columns.length >= 4 && 'grid-cols-2 md:grid-cols-4'
            )}
          >
            {options.columns.map((column, colIndex) => {
              // Get items student placed in this column
              const studentItemsInColumn = Object.entries(placements || {})
                .filter(([_, placedColIndex]) => placedColIndex === colIndex)
                .map(([item]) => item);

              // Get correct items for this column
              const correctItems = column.correctItems || [];

              return (
                <div key={colIndex} className="space-y-2">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">
                    {column.label}
                  </div>
                  <div className="min-h-[80px] p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <div className="flex flex-wrap gap-2">
                      {studentItemsInColumn.length > 0 ? (
                        studentItemsInColumn.map((item) => {
                          const isCorrect = correctItems.includes(item);
                          return (
                            <span
                              key={item}
                              className={cn(
                                'px-3 py-1.5 rounded-lg text-sm font-medium',
                                isCorrect
                                  ? 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
                                  : 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
                              )}
                            >
                              {item}
                              {isCorrect ? (
                                <span className="ml-1 text-green-600 dark:text-green-400">✓</span>
                              ) : (
                                <span className="ml-1 text-red-600 dark:text-red-400">✗</span>
                              )}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500 italic">
                          {t('feedback.noItemsPlaced')}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Show items that should be here but weren't placed */}
                  {correctItems.some(item => !studentItemsInColumn.includes(item)) && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('feedback.missing')}{' '}
                      {correctItems
                        .filter(item => !studentItemsInColumn.includes(item))
                        .map((item, i, arr) => (
                          <span key={item}>
                            <span className="text-amber-600 dark:text-amber-400 font-medium">{item}</span>
                            {i < arr.length - 1 && ', '}
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Non-MCQ Student Response */
        <div className="mb-6">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('feedback.yourResponse')}
          </h4>
          <div className="text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-700 rounded-lg p-4 whitespace-pre-wrap">
            {studentResponse || <span className="text-gray-400 dark:text-gray-500 italic">{t('feedback.noResponse')}</span>}
          </div>
        </div>
      )}

      {/* Model Answer for Short Answer */}
      {questionType === 'short_answer' && modelAnswer && (
        <div className="mb-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-700">
            <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2">
              {t('feedback.modelAnswer')}
            </h4>
            <p className="text-sm text-blue-900 dark:text-blue-200 whitespace-pre-wrap">{modelAnswer}</p>
          </div>
        </div>
      )}

      {/* Feedback (for non-MCQ or additional feedback) */}
      {!isMCQ && feedback && (
        <div className="mb-6">
          <div
            className={cn(
              'relative rounded-lg p-4',
              'bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-900',
              'border-l-4 border-gray-300 dark:border-gray-600'
            )}
          >
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t('feedback.feedbackLabel')}
            </h4>
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {feedback}
            </div>
          </div>
        </div>
      )}

      {/* Skills */}
      {skills && skills.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            {t('feedback.skillsAssessed')}
          </h4>
          <SkillTagList skills={skills} />
        </div>
      )}

      {/* Thinking Process */}
      {thinking && (
        <ExpandableThinking thinking={thinking} />
      )}
    </Card>
  );
}

export function FeedbackPanelCompact({
  questionNumber,
  score,
  maxScore = 100,
  isCorrect,
  className,
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-lg',
        isCorrect ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-700',
        className
      )}
    >
      <span className="text-sm text-gray-700 dark:text-gray-300">
        Question {questionNumber}
      </span>
      <ScoreBadge score={score} maxScore={maxScore} size="sm" />
    </div>
  );
}
